import React, { useEffect, useRef, useState } from "react";
import { LifeBuoy, X, Send, Loader2, ChevronLeft, Wrench, CreditCard, ShieldCheck, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import MarkdownLite from "./MarkdownLite";

const CATS = [
  { key: "technical", label: "Technical Issue", icon: Wrench },
  { key: "billing", label: "Billing", icon: CreditCard },
  { key: "privacy", label: "Data/Privacy", icon: ShieldCheck },
  { key: "feature", label: "Feature Request", icon: Lightbulb },
];

// Realm-agnostic AI support chat. `apiClient` is an axios instance whose baseURL
// already ends in /api; `base` is the realm prefix (e.g. "/support", "/consumer/support",
// "/verify/pro/support").
//   inline=false -> fixed modal overlay (legacy).
//   inline=true  -> an on-page panel that sits in the normal document flow and
//                   matches the surrounding page (pass `accent` to theme it).
export const SupportChat = ({ apiClient, base, onClose, dark = true, inline = false, themed = false, accent = "#cbb26a" }) => {
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await apiClient.get(`${base}/session`);
      setTicket(data.ticket || null);
    } catch { setTicket(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [ticket, sending]);

  const post = async (payload) => {
    setSending(true);
    try {
      const { data } = await apiClient.post(`${base}/message`, payload);
      setTicket(data.ticket);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setSending(false);
  };

  const pickCategory = (c) => post({ text: `I need help with: ${c.label}`, category: c.key });
  const send = async () => {
    const t = text.trim();
    if (!t || sending) return;
    setText("");
    await post({ text: t });
  };

  const msgs = ticket?.messages || [];
  const escalated = ticket?.status === "open";

  if (themed) {
    return (
      <div className="h-full flex flex-col surface" data-testid="support-chat">
        <div className="shrink-0 border-b border-white/8 bg-gradient-to-b from-primary/[0.07] to-transparent flex items-center gap-3 px-5 h-16">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/30 flex items-center justify-center shrink-0"><LifeBuoy size={19} className="text-primary" /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-[15px] tracking-tight">Lexarra Support</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5">
              <span className={`w-1.5 h-1.5 rounded-full ${escalated ? "bg-emerald-500 animate-pulse" : "bg-primary/60"}`} />
              {escalated ? "Connected with our team" : "Usually replies in a moment"}
            </div>
          </div>
          <button onClick={onClose} data-testid="support-chat-close" className="w-8 h-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-black/5 dark:hover:bg-white/5 lift"><X size={18} /></button>
        </div>
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-6 py-6" data-testid="support-chat-messages">
          {loading ? (
            <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-primary" size={22} /></div>
          ) : msgs.length === 0 && !ticket ? (
            <div className="max-w-xl mx-auto text-center pt-6 fade-up">
              <div className="w-16 h-16 rounded-3xl bg-gradient-to-br from-primary/25 to-primary/[0.04] border border-primary/25 flex items-center justify-center mx-auto mb-4"><LifeBuoy size={28} className="text-primary" /></div>
              <h3 className="font-editorial text-2xl sm:text-3xl tracking-tight mb-1.5">How can we help?</h3>
              <p className="text-[13.5px] text-muted-foreground mb-6">Pick a topic to get started, or just type your question below. Tricky issues are handed to our team automatically.</p>
              <div className="grid sm:grid-cols-2 gap-2.5 text-left">
                {CATS.map((c) => {
                  const Icon = c.icon;
                  return (
                    <button key={c.key} disabled={sending} onClick={() => pickCategory(c)} data-testid={`support-cat-${c.key}`}
                      className="group flex items-center gap-3 surface-2 hairline rounded-xl px-4 py-3 lift hover:border-primary/40 disabled:opacity-60">
                      <span className="w-9 h-9 rounded-lg bg-primary/12 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors"><Icon size={16} className="text-primary" /></span>
                      <span className="text-[13.5px] font-medium text-foreground/90">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-4">
              <div className="flex gap-3 fade-up">
                <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/25 flex items-center justify-center shrink-0 mt-0.5"><LifeBuoy size={15} className="text-primary" /></div>
                <div className="text-[14px] text-foreground/90 leading-relaxed surface-2 hairline rounded-2xl rounded-bl-md px-4 py-2.5">
                  Hi{ticket ? "" : " there"}! I'm Lexarra Support — how can I help?
                </div>
              </div>
              {msgs.map((m) => (
                <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "gap-3"} fade-up`}>
                  {m.sender !== "user" && <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary/25 to-primary/5 border border-primary/25 flex items-center justify-center shrink-0 mt-0.5"><LifeBuoy size={15} className="text-primary" /></div>}
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[14px] leading-relaxed whitespace-pre-wrap shadow-sm ${m.sender === "user" ? "bg-primary text-primary-foreground rounded-br-md" : "surface-2 hairline text-foreground/90 rounded-bl-md"}`}>
                    {m.sender === "user" ? m.text : <MarkdownLite text={m.text} />}
                  </div>
                </div>
              ))}
              {sending && <div className="flex items-center gap-1.5 pl-11"><span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "150ms" }} /><span className="w-1.5 h-1.5 rounded-full bg-primary/70 animate-bounce" style={{ animationDelay: "300ms" }} /></div>}
              {escalated && <div className="text-[11.5px] text-center text-muted-foreground py-1" data-testid="support-escalated-note">A support ticket has been created. Our team will reply right here.</div>}
            </div>
          )}
        </div>
        <div className="shrink-0 border-t border-white/8 p-4">
          <div className="max-w-2xl mx-auto flex items-end gap-2 surface-2 hairline rounded-2xl p-1.5 pl-4 focus-within:border-primary/50 transition-colors">
            <textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1} placeholder="Describe your issue…" data-testid="support-chat-input" disabled={sending}
              className="flex-1 bg-transparent py-2 text-sm outline-none resize-none max-h-32 placeholder:text-muted-foreground" />
            <button onClick={send} disabled={sending || !text.trim()} data-testid="support-chat-send"
              className="w-10 h-10 shrink-0 rounded-xl bg-primary text-primary-foreground flex items-center justify-center lift hover:bg-primary/90 disabled:opacity-40 shadow-lg shadow-primary/20"><Send size={16} /></button>
          </div>
        </div>
      </div>
    );
  }

  const card = dark ? "bg-[#0a0d16] border-white/10 text-white" : "bg-white border-black/10 text-[#0b1120]";
  const sub = dark ? "text-white/55" : "text-black/55";
  const aiBubble = dark ? "bg-white/[0.06] text-white/90" : "bg-black/[0.05] text-black/85";

  const body = (
    <div onClick={(e) => e.stopPropagation()}
      className={`w-full flex flex-col overflow-hidden ${inline ? "rounded-2xl border h-[70vh] max-h-[620px]" : "w-[94vw] max-w-md h-[80vh] max-h-[640px] rounded-2xl border shadow-2xl"} ${card}`}>
      <div className="flex items-center justify-between px-4 h-14 border-b border-white/8 shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}26`, border: `1px solid ${accent}55` }}><LifeBuoy size={16} style={{ color: accent }} /></div>
          <div className="leading-tight">
            <div className="font-display font-semibold text-[14px]">Lexarra Support</div>
            <div className={`text-[10.5px] ${sub}`}>{escalated ? "With our team" : "How can we help?"}</div>
          </div>
        </div>
        <button onClick={onClose} data-testid="support-chat-close" className={sub + " hover:opacity-70"}><X size={18} /></button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="support-chat-messages">
        {loading ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin" size={22} style={{ color: accent }} /></div>
        ) : (
          <>
            <div className={`text-[13.5px] leading-relaxed rounded-xl px-3.5 py-2.5 ${aiBubble}`}>
              Hi{ticket ? "" : " there"}! I'm Lexarra Support. {msgs.length === 0 && "What do you need help with today?"}
            </div>
            {msgs.length === 0 && !ticket && (
              <div className="grid grid-cols-2 gap-2 pt-1">
                {CATS.map((c) => (
                  <button key={c.key} disabled={sending} onClick={() => pickCategory(c)} data-testid={`support-cat-${c.key}`}
                    className={`text-[12.5px] font-medium rounded-xl px-3 py-2.5 border lift ${dark ? "border-white/12 hover:bg-white/[0.04]" : "border-black/10 hover:bg-black/[0.03]"}`}>
                    {c.label}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-[13.5px] leading-relaxed whitespace-pre-wrap ${m.sender === "user" ? "font-medium" : aiBubble}`}
                  style={m.sender === "user" ? { background: accent, color: "#0b1120" } : {}}>
                  {m.sender === "user" ? m.text : <MarkdownLite text={m.text} />}
                  <div className={`text-[9.5px] mt-1 ${m.sender === "user" ? "text-[#0b1120]/60" : sub}`}>
                    {m.sender === "user" ? "You" : "Lexarra Support"} · {new Date(m.created_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                  </div>
                </div>
              </div>
            ))}
            {sending && <div className="flex justify-start"><div className={`rounded-xl px-3.5 py-2.5 ${aiBubble}`}><Loader2 className="animate-spin" size={14} /></div></div>}
            {escalated && (
              <div className={`text-[11.5px] text-center py-1 ${sub}`} data-testid="support-escalated-note">
                A support ticket has been created. Our team will reply right here.
              </div>
            )}
          </>
        )}
      </div>

      <div className="p-3 border-t border-white/8 shrink-0 flex items-center gap-2">
        <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
          data-testid="support-chat-input" placeholder="Type your message…" disabled={sending}
          className={`flex-1 h-10 rounded-lg px-3 text-[13px] outline-none ${dark ? "bg-white/[0.05] border border-white/10 text-white placeholder:text-white/35" : "bg-black/[0.03] border border-black/10 placeholder:text-black/35"}`} />
        <button onClick={send} disabled={sending || !text.trim()} data-testid="support-chat-send"
          className="h-10 px-3.5 rounded-lg text-[13px] font-semibold flex items-center gap-1.5 disabled:opacity-50 lift" style={{ background: accent, color: "#0b1120" }}><Send size={14} /></button>
      </div>
    </div>
  );

  if (inline) return <div className="w-full" data-testid="support-chat">{body}</div>;
  return (
    <div className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="support-chat">
      {body}
    </div>
  );
};

export default SupportChat;
